import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { useParams } from 'react-router-dom'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid'
import _ from 'lodash'

// Components
import { Button } from '../../components/Button'
import { DataTable } from '../../components/DataTable'
import { PageContainer } from '../../components/PageContainer'
import { StateContainer } from '../../components/StateContainer'
import { TextInput } from '../../components/TextInput'
import { TextEditor } from '../../components/CustomEditor'

// Services
import { getClient } from '../../services/clients.service'
import {
  deleteClientAccount,
  getAccountPortfolioDataCount,
  getClientAccounts,
  updateClientAccount,
} from '../../services/accounts.service'

// Local
import MergeAccountsModal from './MergeAccountsModal'

// Stores
import { ClientDashboardStoreContext } from '../../stores/ClientDashboardStore'

// Utils & Hooks
import { toast } from '../../utils/helpers'
import { usePagination, useSorting } from '../../hooks/DataTableManagement'

/**
 *
 * Accounts
 *
 */
const Accounts = observer(() => {
  // Context
  const { clientId } = useParams()
  const { setClient } = useContext(ClientDashboardStoreContext)

  // Pagination
  const { pagination, setTotalRecords } = usePagination(10)
  const { perPage, currentPage } = pagination

  // Sorting
  const { sorting } = useSorting('name_sort')
  const { sortedColumn } = sorting

  // State
  const [loading, setLoading] = useState(true)
  const [loadingAccounts, setLoadingAccounts] = useState(false)
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [error, setError] = useState(null)
  const [accounts, setAccounts] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [search, setSearch] = useState('')
  const [portfolioDataCounts, setPortfolioDataCounts] = useState({})
  const [mergeState, setMergeState] = useState(null)

  const BASE_URL = `/clients/${clientId}/accounts/?`
  const searchInputRef = useRef()

  const handleSuccess = (m) => toast(m, 'success')
  const handleErrors = (m) => toast(m, 'error')

  /**
   * Gets the updated list of accounts; updates pagination and portfolio data counts.
   * @param {string} url
   */
  const getUpdatedAccounts = async (url) => {
    const response = await getClientAccounts(url, setError, setLoadingAccounts)

    if (response) {
      setTotalRecords(response.count)
      setAccounts(response.results)

      // Fetch all portfolio data counts concurrently
      const countPromises = response.results.map(account =>
        getAccountPortfolioDataCount(clientId, account.id, () => {}, () => {})
      )
      const countResults = await Promise.all(countPromises)
      const counts = {}
      response.results.forEach((account, idx) => {
        counts[account.id] = countResults[idx]
      })
      setPortfolioDataCounts(counts)
    }
  }

  useEffect(() => {
    if (clientId) {
      const getUpdatedClient = async () => {
        const updatedClient = await getClient(clientId, setError, setLoading)

        if (updatedClient) {
          setClient(updatedClient)
        }
      }

      getUpdatedClient()
    }
  }, [clientId])

  /**
   * When the filter, sort, current page, or row count changes, get the updated list of accounts.
   */
  useEffect(() => {
    if (clientId) {
      getUpdatedAccounts(
        `${BASE_URL}order_by=${sortedColumn}&limit=${perPage}&page=${currentPage}${
          search ? `&q=${search}` : ''
        }`,
      )
    }
  }, [perPage, clientId, sortedColumn, currentPage, search])

  /**
   * Debounces the search input.
   */
  const searchAccounts = useCallback(_.debounce(setSearch, 500), [])

  /**
   * Handles row edit completion by updating the local `account` with new data and
   * submitting it to the backend.
   * @param {object} event
   */
  const onRowEditComplete = async ({ newData, index }) => {
    const oldAccounts = [...accounts]
    const updatedData = [...accounts]
    updatedData[index] = newData

    // Optimistically update the data to prevent showing internal values
    setAccounts(updatedData)

    await updateClientAccount(
      clientId,
      newData,
      (m) => {
        // Reset back to previous value
        setAccounts(oldAccounts)
        handleErrors(m)
      },
      () => {},
      (m) => handleSuccess(m),
      (conflict) => {
        // Duplicate account conflict — revert the optimistic update and prompt the user to merge
        setAccounts(oldAccounts)
        setMergeState({
          source: { id: newData.id, name: newData.name, number: newData.number },
          target: {
            id: conflict.conflictAccountId,
            name: conflict.conflictAccountName,
            number: conflict.conflictAccountNumber,
          },
        })
      },
    )
  }

  const refreshAccounts = () => {
    getUpdatedAccounts(
      `${BASE_URL}order_by=${sortedColumn}&limit=${perPage}&page=${currentPage}${
        search ? `&q=${search}` : ''
      }`,
    )
  }

  return (
    <PageContainer>
      <StateContainer error={error} loading={loading}>
        <div className="flex size-full flex-col overflow-y-auto bg-background">
          <div className="space-y-6">
            <div>
              <h2 className="pt-1 text-xl font-semibold leading-6 text-gray-900">Accounts</h2>

              <TextInput
                className="w-full rounded-full py-2.5 pl-10 pr-4 placeholder:font-normal placeholder:text-gray-600 md:w-[450px]"
                icon={
                  <MagnifyingGlassIcon className="ml-2 h-5 text-gray-dark" aria-hidden="true" />
                }
                id="search"
                endIcon={
                  searchTerm ? (
                    <Button
                      type="button"
                      onClick={() => {
                        setSearchTerm('')
                        searchAccounts(null)
                        searchInputRef.current.value = ''
                      }}
                      icon={<XMarkIcon className="mr-2 h-5 text-gray-dark" aria-hidden="true" />}
                      ariaLabel="Clear search"
                      iconOnly
                    />
                  ) : null
                }
                name="search"
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  searchAccounts(e.target.value)
                }}
                placeholder="Looking for something?"
                ref={searchInputRef}
                value={searchTerm}
              />
            </div>

            <DataTable
              data={accounts}
              columns={[
                {
                  field: 'name',
                  header: 'Name',
                  editor: TextEditor,
                  style: { width: '50%' },
                  sortable: true,
                  sortField: 'name_sort',
                },
                {
                  field: 'number',
                  header: 'Number',
                  editor: TextEditor,
                  style: { width: '50%' },
                  sortable: true,
                  sortField: 'number_sort',
                },
              ]}
              hoverEffect={false}
              loading={loadingAccounts}
              loadingDelete={loadingDelete}
              pagination={pagination}
              sorting={sorting}
              onRowEditComplete={onRowEditComplete}
              onRowDeleteConfirm={(row) => {
                deleteClientAccount(clientId, row.id, handleErrors, setLoadingDelete, () => {
                  handleSuccess('Account deleted.')
                  refreshAccounts()
                })
              }}
              areRowsDeleteable
              areRowsEditable
              rowDeleteConfirm={(row) => {
                const portfolioCount = portfolioDataCounts[row.id] || 0
                const portfolioMessage =
                  portfolioCount > 0
                    ? ` This will delete ${portfolioCount} portfolio data record${
                      portfolioCount === 1 ? '' : 's'
                    } associated with this account.`
                    : ''

                return {
                  title: `Delete Account`,
                  type: 'warning',
                  message: (
                    <div className="flex flex-col">
                      <span className="">Are you sure you want to delete the account:</span>
                      <span className="font-semibold">{row.name}</span>
                      {portfolioMessage && (
                        <div className="mt-2 rounded border-l-4 border-orange-400 bg-orange-50 p-2">
                          <span className="text-orange-800">{portfolioMessage}</span>
                        </div>
                      )}
                    </div>
                  ),
                }
              }}
            />
          </div>
        </div>
      </StateContainer>

      {mergeState && (
        <MergeAccountsModal
          clientId={clientId}
          source={mergeState.source}
          target={mergeState.target}
          closeModal={() => setMergeState(null)}
          onSuccess={() => {
            setMergeState(null)
            refreshAccounts()
          }}
        />
      )}
    </PageContainer>
  )
})

export default Accounts
